import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, formatDate, getCertificateStatus } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { EmailHistoryCard } from '../components/EmailHistoryCard';
import { 
  ArrowLeft, 
  User, 
  Award,
  Mail, 
  Phone,
  MapPin,
  Building2,
  Building,
  Printer,
  Loader2
} from 'lucide-react';

const CertificatePrintView = ({ certificate, onClose }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [classData, setClassData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch templates and class data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch templates
        const templatesRes = await axios.get(`${API_URL}/certificate-templates`);
        const allTemplates = templatesRes.data.items || templatesRes.data || [];
        
        // Filter templates for this course
        const courseTemplates = allTemplates.filter(t => t.course_name === certificate.course_name);
        setTemplates(courseTemplates.length > 0 ? courseTemplates : allTemplates);
        
        // Set default template (first matching course template)
        if (courseTemplates.length > 0) {
          setSelectedTemplate(courseTemplates[0]);
        } else if (allTemplates.length > 0) {
          setSelectedTemplate(allTemplates[0]);
        }
        
        // Fetch class data for additional fields (instructor, declaration, signature, etc.)
        if (certificate.class_id) {
          try {
            const classRes = await axios.get(`${API_URL}/classes/${certificate.class_id}`);
            const cls = classRes.data;
            
            // Fetch instructors for the class
            let instructorNames = '';
            let instructorNumbers = '';
            if (cls.instructor_ids && cls.instructor_ids.length > 0) {
              const instructorsRes = await axios.get(`${API_URL}/instructors`);
              const allInstructors = instructorsRes.data.items || instructorsRes.data || [];
              const classInstructors = allInstructors.filter(i => cls.instructor_ids.includes(i.id));
              // Pick primary instructor (or first) for certificate display
              const primaryId = cls.primary_instructor_id;
              const primary = (primaryId && classInstructors.find(i => i.id === primaryId)) || classInstructors[0];
              if (primary) {
                instructorNames = `${primary.first_name} ${primary.last_name}`;
                instructorNumbers = primary.instructor_number || '';
              }
            }
            
            // Fetch training center name
            let trainingCenterName = '';
            if (cls.training_center_id) {
              try {
                const tcRes = await axios.get(`${API_URL}/training-centers/${cls.training_center_id}`);
                trainingCenterName = tcRes.data.name;
              } catch (e) {
                console.log('Could not fetch training center');
              }
            }
            
            // Fetch declaration text
            let declarationText = '';
            if (cls.declaration_id) {
              try {
                const declRes = await axios.get(`${API_URL}/declarations/${cls.declaration_id}`);
                declarationText = declRes.data.text;
              } catch (e) {
                console.log('Could not fetch declaration');
              }
            }
            
            setClassData({
              ...cls,
              instructor_names: instructorNames,
              instructor_numbers: instructorNumbers,
              training_center_name: trainingCenterName,
              declaration_text: declarationText,
              signature_url: cls.signature_path || null,
              extra_text: cls.extra_text || null
            });
          } catch (e) {
            console.log('Could not fetch class data:', e);
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load print templates');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [certificate]);

  const handlePrint = () => {
    const printWindow = window.open('', '_blank', 'width=800,height=600');
    
    if (!printWindow) {
      alert('Please allow popups for this site to print certificates');
      return;
    }
    
    // If we have a template with positions, use template-based printing
    if (selectedTemplate) {
      const orientation = selectedTemplate.page_orientation || 'portrait';
      const pageWidth = orientation === 'landscape' ? 297 : 210; // A4 in mm
      const pageHeight = orientation === 'landscape' ? 210 : 297;
      
      // Helper to generate positioned text
      const renderField = (fieldConfig, value) => {
        if (!fieldConfig || !fieldConfig.enabled || !value) return '';
        const { x, y, font_size, font_weight, text_align } = fieldConfig;
        return `
          <div style="
            position: absolute;
            left: ${x}mm;
            top: ${y}mm;
            font-size: ${font_size || 12}pt;
            font-weight: ${font_weight || 'normal'};
            text-align: ${text_align || 'left'};
            ${text_align === 'center' ? 'transform: translateX(-50%);' : ''}
            ${text_align === 'right' ? 'transform: translateX(-100%);' : ''}
          ">${value}</div>
        `;
      };
      
      // Helper to generate positioned image (for signature)
      const renderImage = (fieldConfig, imageUrl) => {
        if (!fieldConfig || !fieldConfig.enabled || !imageUrl) return '';
        const { x, y, font_size } = fieldConfig;
        const height = font_size ? font_size * 2 : 30; // Default 30mm height
        return `
          <img src="${imageUrl}" style="
            position: absolute;
            left: ${x}mm;
            top: ${y}mm;
            height: ${height}mm;
            object-fit: contain;
          " />
        `;
      };
      
      // Construct signature URL
      const signatureFullUrl = classData?.signature_url 
        ? window.location.origin + classData.signature_url
        : null;
      
      printWindow.document.write(`
        <html>
          <head>
            <title>Certificate - ${certificate.certificate_number}</title>
            <style>
              @page {
                size: ${orientation};
                margin: 0;
              }
              body {
                font-family: 'Inter', Arial, sans-serif;
                margin: 0;
                padding: 0;
              }
              .certificate-page {
                position: relative;
                width: ${pageWidth}mm;
                height: ${pageHeight}mm;
                overflow: hidden;
              }
              @media print {
                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              }
            </style>
          </head>
          <body>
            <div class="certificate-page">
              ${renderField(selectedTemplate.certificate_number, certificate.certificate_number)}
              ${renderField(selectedTemplate.student_name, certificate.person_name)}
              ${renderField(selectedTemplate.course_name, certificate.outcome_name || certificate.course_name)}
              ${renderField(selectedTemplate.company_name, certificate.company_name)}
              ${renderField(selectedTemplate.issue_date, new Date(certificate.issue_date).toLocaleDateString('en-GB'))}
              ${renderField(selectedTemplate.expiry_date, certificate.expiry_date ? new Date(certificate.expiry_date).toLocaleDateString('en-GB') : 'No Expiration')}
              ${renderField(selectedTemplate.instructor_names, classData?.instructor_names)}
              ${renderField(selectedTemplate.instructor_numbers, classData?.instructor_numbers)}
              ${renderField(selectedTemplate.training_center_name, classData?.training_center_name)}
              ${renderField(selectedTemplate.duration_hours, classData?.duration_hours ? `${classData.duration_hours} hours` : null)}
              ${renderField(selectedTemplate.declaration_text, classData?.declaration_text)}
              ${renderField(selectedTemplate.extra_text, classData?.extra_text)}
              ${renderImage(selectedTemplate.signature, signatureFullUrl)}
            </div>
          </body>
        </html>
      `);
    } else {
      // Fallback: basic HTML template if no print template is available
      printWindow.document.write(`
        <html>
          <head>
            <title>Certificate - ${certificate.certificate_number}</title>
            <style>
              body {
                font-family: 'Inter', Arial, sans-serif;
                padding: 40px;
                max-width: 800px;
                margin: 0 auto;
              }
              .certificate-header {
                text-align: center;
                margin-bottom: 40px;
                padding-bottom: 20px;
                border-bottom: 3px solid #2C9DE1;
              }
              .certificate-header img {
                max-height: 60px;
              }
              .certificate-field {
                margin: 20px 0;
                padding: 15px 0;
                border-bottom: 1px solid #e5e5e5;
              }
              .certificate-field label {
                font-weight: 600;
                color: #666;
                font-size: 11px;
                text-transform: uppercase;
                letter-spacing: 1px;
              }
              .certificate-field p {
                font-size: 18px;
                margin: 8px 0 0 0;
                color: #111;
              }
              .certificate-number {
                font-family: monospace;
                font-size: 28px;
                color: #2C9DE1;
              }
              @media print {
                body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
              }
            </style>
          </head>
          <body>
            <div class="certificate-header">
              <img src="${window.location.origin}/logo.png" alt="EMP Medic" />
              <p style="color: #666; font-size: 14px; margin-top: 10px;">Training Certificate</p>
            </div>
            <div class="certificate-field">
              <label>Certificate Number</label>
              <p class="certificate-number">${certificate.certificate_number}</p>
            </div>
            <div class="certificate-field">
              <label>Participant Name</label>
              <p>${certificate.person_name}</p>
            </div>
            <div class="certificate-field">
              <label>Course</label>
              <p>${certificate.outcome_name || certificate.course_name}</p>
            </div>
            ${certificate.company_name ? `
            <div class="certificate-field">
              <label>Company</label>
              <p>${certificate.company_name}</p>
            </div>
            ` : ''}
            ${classData?.training_center_name ? `
            <div class="certificate-field">
              <label>Training Center</label>
              <p>${classData.training_center_name}</p>
            </div>
            ` : ''}
            ${classData?.instructor_names ? `
            <div class="certificate-field">
              <label>Instructor(s)</label>
              <p>${classData.instructor_names}</p>
              ${classData?.instructor_numbers ? `<p style="font-size: 12px; color: #666;">#${classData.instructor_numbers}</p>` : ''}
            </div>
            ` : ''}
            ${classData?.declaration_text ? `
            <div class="certificate-field">
              <label>Declaration</label>
              <p style="font-size: 14px; color: #444;">${classData.declaration_text}</p>
            </div>
            ` : ''}
            <div style="display: flex; gap: 40px;">
              <div class="certificate-field" style="flex: 1;">
                <label>Issue Date</label>
                <p>${new Date(certificate.issue_date).toLocaleDateString('en-GB')}</p>
              </div>
              <div class="certificate-field" style="flex: 1;">
                <label>Expiry Date</label>
                <p>${certificate.expiry_date ? new Date(certificate.expiry_date).toLocaleDateString('en-GB') : 'No Expiration'}</p>
              </div>
            </div>
            ${classData?.signature_url ? `
            <div class="certificate-field" style="margin-top: 30px;">
              <label>Signature</label>
              <img src="${window.location.origin}${classData.signature_url}" style="max-height: 60px; margin-top: 10px;" />
            </div>
            ` : ''}
          </body>
        </html>
      `);
    }
    
    printWindow.document.close();
    
    // Wait for content to load before printing
    printWindow.onload = function() {
      printWindow.focus();
      printWindow.print();
    };
    
    // Fallback: try printing after a short delay if onload doesn't fire
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 500);
  };

  const status = getCertificateStatus(certificate.expiry_date);

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
            Certificate Details
          </DialogTitle>
        </DialogHeader>
        
        <div className="p-6">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 mb-2">
              <img src="/logo.png" alt="EMP Medic" className="h-12" />
            </div>
            <p className="text-slate-500 text-sm">Training Certificate</p>
          </div>

          <div className="space-y-4">
            <div className="border-b pb-3">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Certificate Number</label>
              <p className="text-2xl font-mono text-[#2C9DE1] mt-1">{certificate.certificate_number}</p>
            </div>

            <div className="border-b pb-3">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Participant Name</label>
              <p className="text-xl font-semibold text-slate-900 mt-1">{certificate.person_name}</p>
            </div>

            <div className="border-b pb-3">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Course</label>
              <p className="text-lg text-slate-900 mt-1">{certificate.outcome_name || certificate.course_name}</p>
            </div>

            {certificate.company_name && (
              <div className="border-b pb-3">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Company</label>
                <p className="text-lg text-slate-900 mt-1">{certificate.company_name}</p>
              </div>
            )}

            {classData?.training_center_name && (
              <div className="border-b pb-3">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Training Center</label>
                <p className="text-lg text-slate-900 mt-1">{classData.training_center_name}</p>
              </div>
            )}

            {classData?.instructor_names && (
              <div className="border-b pb-3">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Instructor(s)</label>
                <p className="text-lg text-slate-900 mt-1">{classData.instructor_names}</p>
                {classData?.instructor_numbers && (
                  <p className="text-sm text-slate-500 mt-1">#{classData.instructor_numbers}</p>
                )}
              </div>
            )}

            {classData?.declaration_text && (
              <div className="border-b pb-3">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Declaration</label>
                <p className="text-slate-700 mt-1 text-sm">{classData.declaration_text}</p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="border-b pb-3">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Issue Date</label>
                <p className="text-lg text-slate-900 mt-1">{formatDate(certificate.issue_date)}</p>
              </div>

              <div className="border-b pb-3">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Expiry Date</label>
                <p className="text-lg text-slate-900 mt-1">{certificate.expiry_date ? formatDate(certificate.expiry_date) : 'No Expiration'}</p>
              </div>
            </div>

            <div className="mt-4 flex items-center gap-4">
              <Badge 
                className={`
                  ${!certificate.expiry_date ? 'bg-blue-100 text-blue-800' : ''}
                  ${certificate.expiry_date && status === 'active' ? 'bg-emerald-100 text-emerald-800' : ''}
                  ${status === 'expiring' ? 'bg-amber-100 text-amber-800' : ''}
                  ${status === 'expired' ? 'bg-red-100 text-red-800' : ''}
                `}
              >
                {!certificate.expiry_date ? 'Lifetime Valid' : status === 'active' ? 'Valid' : status === 'expiring' ? 'Expiring Soon' : 'Expired'}
              </Badge>
            </div>

            {/* Template selector */}
            {templates.length > 0 && (
              <div className="mt-4 pt-4 border-t">
                <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-2">Print Template</label>
                <Select 
                  value={selectedTemplate?.id || ''} 
                  onValueChange={(value) => {
                    const template = templates.find(t => t.id === value);
                    setSelectedTemplate(template);
                  }}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select a print template" />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map(template => (
                      <SelectItem key={template.id} value={template.id}>
                        {template.name} ({template.page_orientation || 'portrait'})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-4 px-6 pb-4">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button 
            className="bg-[#2C9DE1] hover:bg-[#1A7BB8] text-white"
            onClick={handlePrint}
            disabled={loading}
          >
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Printer className="w-4 h-4 mr-2" />
            )}
            {selectedTemplate ? 'Print with Template' : 'Print'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const StudentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [student, setStudent] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [trainingCenters, setTrainingCenters] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [studentRes, certsRes, companiesRes, centersRes] = await Promise.all([
          axios.get(`${API_URL}/persons/${id}`),
          axios.get(`${API_URL}/certificates/person/${id}`),
          axios.get(`${API_URL}/companies/lookup?limit=5000`),
          axios.get(`${API_URL}/training-centers`)
        ]);

        setStudent(studentRes.data);
        setCertificates(certsRes.data);
        setCompanies(companiesRes.data.items || companiesRes.data);
        setTrainingCenters(centersRes.data);

      } catch (error) {
        toast.error('Failed to fetch student details');
        navigate('/students');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, navigate]);

  const getCompanyNames = () => {
    if (!student) return null;
    const companyIds = student.company_ids || (student.company_id ? [student.company_id] : []);
    if (companyIds.length === 0) return null;
    return companyIds.map(cid => {
      const company = companies.find(c => c.id === cid);
      return company?.name;
    }).filter(Boolean).join(', ');
  };

  const getTrainingCenterNames = () => {
    if (!student) return null;
    const centerIds = student.training_center_ids || (student.training_center_id ? [student.training_center_id] : []);
    if (centerIds.length === 0) return null;
    return centerIds.map(cid => {
      const center = trainingCenters.find(c => c.id === cid);
      return center?.name;
    }).filter(Boolean).join(', ');
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-6">
        <div className="h-8 bg-slate-200 rounded w-48" />
        <div className="h-64 bg-slate-200 rounded-xl" />
      </div>
    );
  }

  if (!student) {
    return null;
  }

  const companyNames = getCompanyNames();
  const trainingCenterNames = getTrainingCenterNames();

  return (
    <div className="space-y-6 animate-fade-in" data-testid="student-detail">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={() => navigate('/students')}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
      </div>

      {/* Student Info Card */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-slate-100 rounded-xl flex items-center justify-center">
                <User className="w-8 h-8 text-slate-600" />
              </div>
              <div>
                <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {student.first_name} {student.last_name}
                </CardTitle>
                <CardDescription className="mt-1 font-mono">
                  ID: {student.id_number}
                </CardDescription>
                {student.position && (
                  <p className="text-sm text-slate-600 mt-1">{student.position}</p>
                )}
              </div>
            </div>
            <Button 
              variant="outline"
              onClick={() => navigate(`/students?edit=${student.id}`)}
              data-testid="edit-student-btn"
            >
              Edit Student
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {student.email && (
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500 uppercase">Work Email</p>
                  <p className="font-medium">{student.email}</p>
                </div>
              </div>
            )}
            {student.personal_email && (
              <div className="flex items-center gap-3">
                <Mail className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500 uppercase">Personal Email</p>
                  <p className="font-medium">{student.personal_email}</p>
                </div>
              </div>
            )}
            {student.mobile && (
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500 uppercase">Mobile</p>
                  <p className="font-medium">{student.mobile}</p>
                </div>
              </div>
            )}
            {student.telephone && (
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500 uppercase">Telephone</p>
                  <p className="font-medium">{student.telephone}</p>
                </div>
              </div>
            )}
            {(student.city || student.country) && (
              <div className="flex items-center gap-3">
                <MapPin className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500 uppercase">Location</p>
                  <p className="font-medium">
                    {[student.city, student.country].filter(Boolean).join(', ')}
                  </p>
                </div>
              </div>
            )}
            {companyNames && (
              <div className="flex items-center gap-3">
                <Building2 className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500 uppercase">Company</p>
                  <p className="font-medium">{companyNames}</p>
                </div>
              </div>
            )}
            {trainingCenterNames && (
              <div className="flex items-center gap-3">
                <Building className="w-5 h-5 text-slate-400" />
                <div>
                  <p className="text-xs text-slate-500 uppercase">Training Center</p>
                  <p className="font-medium">{trainingCenterNames}</p>
                </div>
              </div>
            )}
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-slate-400" />
              <div>
                <p className="text-xs text-slate-500 uppercase">Email Permission</p>
                <p className="font-medium">{student.email_permission ? 'Yes' : 'No'}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Certificates */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5 text-[#2C9DE1]" />
                Certificates
              </CardTitle>
              <CardDescription>
                All certificates issued to this student
              </CardDescription>
            </div>
            <Badge variant="secondary" className="text-lg px-3 py-1">
              {certificates.length}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {certificates.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Award className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <h3 className="text-lg font-medium text-slate-900">No certificates yet</h3>
              <p className="mt-1">Certificates will appear here after course completion</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Certificate #</TableHead>
                  <TableHead>Course</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Issue Date</TableHead>
                  <TableHead>Expiry Date</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {certificates.map((cert) => {
                  const status = getCertificateStatus(cert.expiry_date);
                  return (
                    <TableRow key={cert.id}>
                      <TableCell className="font-mono text-[#2C9DE1]">
                        {cert.certificate_number}
                      </TableCell>
                      <TableCell className="font-medium">{cert.course_name}</TableCell>
                      <TableCell>{cert.company_name || '-'}</TableCell>
                      <TableCell>{formatDate(cert.issue_date)}</TableCell>
                      <TableCell>{formatDate(cert.expiry_date)}</TableCell>
                      <TableCell>
                        <Badge 
                          className={`
                            ${status === 'active' ? 'bg-emerald-100 text-emerald-800' : ''}
                            ${status === 'expiring' ? 'bg-amber-100 text-amber-800' : ''}
                            ${status === 'expired' ? 'bg-red-100 text-red-800' : ''}
                          `}
                        >
                          {status === 'active' ? 'Active' : status === 'expiring' ? 'Expiring' : 'Expired'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      <EmailHistoryCard personId={student.id} />
    </div>
  );
};

export default StudentDetail;
